import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import CoursesPage, {
  Container,
} from '@fuww/library/src/Layouts/CoursesPage';
import Anchor from '@fuww/library/src/Anchor';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import Head from '../components/Head';
import CourseList from '../components/Education/CourseList';
import coursesQuery from '../lib/queries/courses/coursesQuery.graphql';
import courseCategoriesQuery
from '../lib/queries/courses/courseCategoriesQuery.graphql';
import CategorySelect from '../components/Education/CourseCategories';
import withQuery from '../lib/withQuery';
import Error from '../components/Error';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import debouncedReplaceRoute from '../lib/debouncedReplaceRoute';
import withApollo from '../lib/apollo';
import replaceRoute from '../lib/replaceRoute';

export const ALL_CATEGORIES = 1;
const LIMIT = 20;

const headerImageUrls = {
  mobile: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=34,quality=10,width=42/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-1-gat68nw1-2023-05-24.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=288,quality=70,width=360/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-1-gat68nw1-2023-05-24.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=576,quality=70,width=720/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-1-gat68nw1-2023-05-24.png',
  ],
  desktop: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=8,quality=10,width=42/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-vh260fkz-2023-05-24.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=277,quality=70,width=1350/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-vh260fkz-2023-05-24.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=526,quality=70,width=2560/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-vh260fkz-2023-05-24.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=1052,quality=70,width=5120/https://fashionunited.com/img/upload/2023/05/24/fashionunited-courses-vh260fkz-2023-05-24.png',
  ],
};

const Courses = ({
  data, variables, fetchMore, loading,
}) => {
  const { coursesConnection: { edges } } = data;
  return (
    edges.length > 0
      ? (
        <CourseList
          data={data}
          connectionField="coursesConnection"
          variables={variables}
          fetchMore={fetchMore}
          loading={loading}
        />
      )
      : (
        <Container>
          <Error error="No courses listed" />
        </Container>
      )
  );
};

Courses.propTypes = {
  data: PropTypes.shape({
    coursesConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  fetchMore: PropTypes.func,
  variables: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
};

Courses.defaultProps = {
  data: {},
  variables: null,
  fetchMore: undefined,
};

const CourseListWithQuery = withQuery(Courses);

const CoursesListPage = () => {
  const {
    query: {
      categoryId: categoryIdParameter,
      keywords: keywordsParameter,
    },
  } = useRouter();
  const categoryId = categoryIdParameter ? Number(categoryIdParameter) : null;
  const breadcrumbs = [
    <HomeLink />,
    <Anchor
      styled
      href="https://fashionunited.com/education"
    >
      Education
    </Anchor>,
    <LinkAndAnchor
      passHref
      route="courses"
    >
      Courses
    </LinkAndAnchor>,
  ];
  const [keywords, setKeywords] = useState('');

  useEffect(() => setKeywords(keywordsParameter || ''), [keywordsParameter]);

  const handleOnChange = useCallback((event) => {
    const value = Number(event.target.value);

    const currentCategoryId = value === ALL_CATEGORIES ? null : value;
    replaceRoute('courses', {
      categoryId: currentCategoryId,
      keywords: keywordsParameter,
    });
  }, [keywordsParameter]);

  return (
    <CoursesPage
      title="FashionUnited Courses"
      breadcrumbs={breadcrumbs}
      keywords={keywords}
      onChange={({ target: { value } }) => {
        setKeywords(value);
        debouncedReplaceRoute(
          'courses',
          value ? { keywords: value, categoryId } : { categoryId },
        );
      }}
      onClear={() => {
        setKeywords('');
        replaceRoute('courses', { categoryId });
      }}
      categorySelect={(
        <CategorySelect
          query={courseCategoriesQuery}
          value={categoryId || ALL_CATEGORIES}
          handleOnChange={handleOnChange}
          errorMessage="Error loading category list"
        />
      )}
    >
      <Head
        title="FashionUnited Courses"
        description="FashionUnited Courses"
      />
      <CourseListWithQuery
        query={coursesQuery}
        variables={{
          categoryId,
          first: LIMIT,
          keywords: keywordsParameter,
        }}
        errorMessage="Error loading courses"
      />
    </CoursesPage>
  );
};

CoursesListPage.getLayout = (page) => (
  <Layout header={(<Hero imageUrls={headerImageUrls} />)}>
    {page}
  </Layout>
);

export default withApollo(CoursesListPage);
